/**
 * Rate summary from its tag codes (never from the Beds24 text): shared by the rate cards (cards.js) and « Votre
 * séjour » (recap.js, 0.10.0), so both show the same cancellation line. Pure, no DOM except cancelText() and
 * paymentText().
 */

/**
 * Short summary of a rate from its tag codes (never from the Beds24 text), joined on one line in the rate box
 * (1.9.0, results declutter change):
 * - cancel: « Annulable jusqu'au 14 mars » (the server's `until`, the page only formats it; Phase 3: no « J-30 »
 *   fallback, the server always sends `until`) or « Non annulable »; [catalogue key, values] or null;
 * - payment: « Acompte 30 % » / « Paiement intégral » (+ « non modifiable » for a never-cancellable rate);
 *   {key, values, percent?, noChanges?} or null.
 * No breakfast since 1.9.0 (Jacques, 22 Oct 2026: Beds24 has no meal plan on a rate; the rate name says it).
 * @param {Array<{code: string, until?: string, percent?: number}>} tags
 */
export function rateSummary(tags) {
	const has = (code) => tags.find((t) => t.code === code);
	const free = has('FREE_CANCELLATION');
	const deposit = has('DEPOSIT');
	const cancel = free
		? /^\d{4}-\d{2}-\d{2}$/.test(free.until || '') ? ['ui.sumFreeCancellationUntil', [free.until]] : null
		: has('NON_REFUNDABLE') || has('DEPOSIT_NON_REFUNDABLE') || has('FULL_PAYMENT_NON_REFUNDABLE') ? ['ui.sumNonRefundable', []] : null;
	const payment = deposit
		? { key: 'ui.sumDeposit', values: [deposit.percent], percent: true }
		: has('FULL_PAYMENT') || has('FULL_PAYMENT_NON_REFUNDABLE') ? { key: 'ui.sumFullPayment', values: [], noChanges: Boolean(has('NON_REFUNDABLE')) } : null;
	return { cancel, payment };
}

/**
 * The payment part as a fragment: « Acompte 30 % » (percent by Intl), « Paiement intégral » (+ « · non modifiable »).
 * @param {Object} i18n
 * @param {?Object} payment rateSummary().payment
 * @returns {?DocumentFragment}
 */
export function paymentText(i18n, payment) {
	if (!payment) {
		return null;
	}
	const text = i18n.t(payment.key, payment.percent ? payment.values.map((v) => i18n.percent(v)) : payment.values);
	if (!payment.noChanges) {
		return text;
	}
	const out = document.createDocumentFragment();
	out.append(text, i18n.sep(' · '), i18n.t('ui.sumNoChanges'));
	return out;
}

/**
 * The cancellation line as a fragment: the date as Intl formats it in the page language (day + short month).
 * @param {Object} i18n
 * @param {?Array} cancel rateSummary().cancel
 * @returns {?DocumentFragment}
 */
export function cancelText(i18n, cancel) {
	if (!cancel) {
		return null;
	}
	return cancel[0] === 'ui.sumFreeCancellationUntil'
		? i18n.t(cancel[0], [i18n.date(cancel[1][0], 'dayMonth')])
		: i18n.t(cancel[0], cancel[1]);
}
